import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  Bot as RobotChipIcon,
  ChevronDown as CaretDownIcon,
  ChevronRight as CaretRightIcon,
  Cpu as ControllerChipIcon,
  MapPin as AreaChipIcon,
  Radio as RadioIcon,
  Shield as ShieldIcon
} from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { decodeRouteParam, plantProtectiveFunctionHref } from '@atbash-labs/plantview-links'
import {
  useGetPlantEquipmentDetailQuery,
  useGetPlantEquipmentProtectiveFunctionsQuery,
  useGetPlantEquipmentSafetyPointsQuery,
  useGetPlantEquipmentRelationshipsQuery,
  usePlantSessionPaint,
  collectAdditionalProperties,
  protectiveFunctionTypeLabel,
  type PlantArea,
  type PlantComputeRef,
  type PlantEquipment,
  type PlantEquipmentProperty,
  type PlantEquipmentProtectiveFunctionLink,
  type PlantEquipmentSafetyPointLink,
  type PlantProtectiveFunctionLinkRole,
  type PlantRobotControllerRef,
  type PlantUdtInstance,
  type PlantUdtMember
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Empty } from '../../components/primitives/Empty'
import { plantEquipmentIndexHref, plantSafetyIndexHref } from '../../lib/plant-links'
import { computeIconFor } from './compute-icon'
import { equipmentIconFor } from './equipment-icon'
import { collectEquipmentSpecs } from './equipment-specs'
import {
  BomSection,
  DeletedRelationshipsSection,
  FaultKnowledgeSection,
  MesUsageSection,
  OpenProblemsSection,
  PhysicalConnectionsSection,
  ProceduresSection,
  SubEquipmentSection,
  UtilitiesSection
} from './equipment-relationships'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { PlantHref } from './PlantHref'
import { LiveTagPanel } from './LiveTagPanel'
import {
  SessionBreadcrumbCurrent,
  SessionEntityMark,
  SessionTitleMark,
  sessionDetailHeaderClass
} from './session-mark-ui'
import { SessionInlinePropertyChanges } from './session-property-changes'

/**
 * `/plant/equipment/items/:equipmentId` — single equipment detail.
 *
 * Renders an equipment-icon header, properties tables grouped by
 * Observable / Setpoint / EquipmentProperty (deterministic SCADA
 * ingest output pending classification), a specifications grid for
 * the equipment node's free-form properties, and a sticky live-tag
 * panel on the right.
 *
 * Live values are shown in the graph-ID-backed panel beside the property
 * tables.
 */
const EquipmentDetailPage = (): React.ReactElement => {
  const { equipmentId = '' } = useParams<{ equipmentId: string }>()
  const decodedEquipmentId = decodeRouteParam(equipmentId)

  const { data, isLoading, isError } = useGetPlantEquipmentDetailQuery(decodedEquipmentId, {
    skip: !decodedEquipmentId
  })
  const relationshipsQuery = useGetPlantEquipmentRelationshipsQuery(decodedEquipmentId, {
    skip: !decodedEquipmentId
  })
  const relationships = relationshipsQuery.data
  const { data: safetyLinks = [] } = useGetPlantEquipmentProtectiveFunctionsQuery(
    decodedEquipmentId,
    {
      skip: !decodedEquipmentId
    }
  )
  const { data: safetyPoints = [] } = useGetPlantEquipmentSafetyPointsQuery(decodedEquipmentId, {
    skip: !decodedEquipmentId
  })
  const safetyHref =
    safetyLinks.length === 1 && safetyLinks[0]
      ? plantProtectiveFunctionHref(safetyLinks[0].function.id)
      : plantSafetyIndexHref()

  const equipment = data?.equipment
  const tags = React.useMemo(() => data?.tags ?? [], [data?.tags])
  const properties = React.useMemo(() => data?.properties ?? [], [data?.properties])
  const udtInstances = React.useMemo(() => data?.udtInstances ?? [], [data?.udtInstances])
  const observables = React.useMemo(
    () => properties.filter((p) => p.kind === 'observable'),
    [properties]
  )
  const setpoints = React.useMemo(
    () => properties.filter((p) => p.kind === 'setpoint'),
    [properties]
  )
  const unclassified = React.useMemo(
    () => properties.filter((p) => p.kind === 'property'),
    [properties]
  )
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('Equipment', equipment?.properties ?? {}),
    [equipment?.properties]
  )
  const Icon = equipmentIconFor(equipment?.equipment_type)
  const { markFor } = usePlantSessionPaint()
  const equipmentMark = markFor(decodedEquipmentId)

  return (
    <div className="relative grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
      <div className="flex min-w-0 flex-col gap-card">
        <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
          <Link to={plantEquipmentIndexHref()} className="hover:text-ink hover:underline">
            Sites
          </Link>
          {data?.site && (
            <>
              <span className="mx-1.5 text-ink-4">/</span>
              <PlantHref
                id={data.site.id}
                label="Site"
                node={data.site}
                className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
              >
                {data.site.name}
                <SessionEntityMark entityId={data.site.id} />
              </PlantHref>
            </>
          )}
          {data?.line && data?.site && (
            <>
              <span className="mx-1.5 text-ink-4">/</span>
              <PlantHref
                id={data.line.id}
                label="Line"
                node={{ ...data.line, siteId: data.site.id }}
                className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
              >
                {data.line.name}
                <SessionEntityMark entityId={data.line.id} />
              </PlantHref>
            </>
          )}
          {(data?.parentEquipment ?? []).map((ancestor) => (
            <React.Fragment key={ancestor.id}>
              <span className="mx-1.5 text-ink-4">/</span>
              <PlantHref
                id={ancestor.id}
                label="Equipment"
                node={ancestor}
                className="inline-flex items-center gap-1.5 hover:text-ink hover:underline"
              >
                {ancestor.name}
                <SessionEntityMark entityId={ancestor.id} />
              </PlantHref>
            </React.Fragment>
          ))}
          <span className="mx-1.5 text-ink-4">/</span>
          <SessionBreadcrumbCurrent entityId={decodedEquipmentId}>
            {equipment?.name ?? decodedEquipmentId}
          </SessionBreadcrumbCurrent>
        </nav>

        {isError ? (
          <DetailFallback
            title="Couldn't load this equipment"
            hint="The equipment may have been deleted, or the proxy is unreachable."
          />
        ) : isLoading || !data || !equipment ? (
          <DetailFallback title="Loading equipment…" hint="" muted />
        ) : (
          <>
            <header
              className={sessionDetailHeaderClass(
                'flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4',
                equipmentMark
              )}
            >
              <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
                <Icon size={24} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="m-0 truncate text-2xl font-semibold text-ink">{equipment.name}</h1>
                  <SessionTitleMark mark={equipmentMark} />
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  {equipment.equipment_type && (
                    <Badge variant="outline">{equipment.equipment_type}</Badge>
                  )}
                  {equipment.vendor && <Badge variant="outline">{equipment.vendor}</Badge>}
                  {data.line && <span className="text-11 text-ink-3">on {data.line.name}</span>}
                  {data.process && (
                    <PlantHref
                      id={data.process.id}
                      label="Process"
                      node={data.process}
                      className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand-soft px-2 py-0.5 text-11 font-medium text-brand no-underline hover:bg-brand-soft/70"
                    >
                      <span aria-hidden>↳</span>
                      {data.process.name}
                      <SessionEntityMark entityId={data.process.id} />
                    </PlantHref>
                  )}
                  <AreaChips areas={relationships?.areas ?? []} />
                  {data.controller && (
                    <PlantHref
                      id={data.controller.id}
                      label="Controller"
                      node={data.controller}
                      className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/10 px-2 py-0.5 text-11 font-medium text-violet-600 no-underline hover:bg-violet-500/20 dark:text-violet-300"
                      title={`Controlled by PLC ${data.controller.name}`}
                    >
                      <ControllerChipIcon size={11} aria-hidden />
                      {data.controller.name}
                      <SessionEntityMark entityId={data.controller.id} />
                    </PlantHref>
                  )}
                  <RobotControllerChips controllers={data.robotControllers} />
                  <ComputeChips computes={data.computes} />
                  <SafetyFunctionChips links={safetyLinks} />
                  <SafetyPointChips links={safetyPoints} />
                  <PlantStrayPill isStray={data.isStray} />
                </div>
                <div className="mt-1.5 font-mono text-11 text-ink-4">{equipment.id}</div>
              </div>
              <Button asChild variant="outline" size="sm" className="shrink-0">
                <Link to={safetyHref}>
                  <ShieldIcon />
                  Safety Requirements
                </Link>
              </Button>
            </header>

            <OpenProblemsSection equipmentId={equipment.id} />
            <SubEquipmentSection equipment={relationships?.subEquipment ?? []} />
            <PhysicalConnectionsSection connections={relationships?.connections ?? []} />
            <EquipmentSafetySection links={safetyLinks} />
            <EquipmentSafetyPointsSection links={safetyPoints} />

            <PropertiesSection
              observables={observables}
              setpoints={setpoints}
              unclassified={unclassified}
            />

            <UdtInstancesSection instances={udtInstances} />

            <UtilitiesSection utilities={data.utilities} utilityEdges={data.utilityEdges} />
            <ProceduresSection procedures={relationships?.procedures ?? []} />
            <FaultKnowledgeSection faults={relationships?.faults ?? []} aboutId={equipment.id} />
            <MesUsageSection
              mesUsage={
                relationships?.mesUsage ?? {
                  recipes: [],
                  phases: [],
                  batches: [],
                  operations: []
                }
              }
            />

            <SpecsSection equipment={equipment} />

            <BomSection
              equipmentId={equipment.id}
              parts={relationships?.bomParts ?? []}
              edges={relationships?.bomEdges ?? []}
              truncated={relationships?.bomTruncated ?? false}
            />

            <DeletedRelationshipsSection entityId={decodedEquipmentId} />

            <EntityDetailsInspector
              entity={equipment}
              additionalProperties={additionalProperties}
            />
          </>
        )}
      </div>

      <LiveTagPanel
        title="Live tag values"
        tags={tags}
        properties={properties}
        scopeEntityId={decodedEquipmentId}
      />
    </div>
  )
}

/**
 * Header chips linking to the Compute host(s) that serve this asset's
 * telemetry via `MONITORED_BY` (an OPC server / device / gateway). The
 * inverse navigation — from a compute back to the assets it monitors —
 * lives in the "Monitored equipment & lines" section on the compute page.
 */
function AreaChips({ areas }: { areas: PlantArea[] }): React.ReactElement | null {
  if (!areas || areas.length === 0) return null
  return (
    <>
      {areas.map((area) => (
        <PlantHref
          key={area.id}
          id={area.id}
          label="Area"
          node={area}
          className="inline-flex items-center gap-1 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-2 py-0.5 text-11 font-medium text-cyan-700 no-underline hover:bg-cyan-500/20 dark:text-cyan-300"
          title={`Located in ${area.name}`}
        >
          <AreaChipIcon size={11} aria-hidden />
          {area.name}
          <SessionEntityMark entityId={area.id} />
        </PlantHref>
      ))}
    </>
  )
}

function ComputeChips({ computes }: { computes: PlantComputeRef[] }): React.ReactElement | null {
  if (!computes || computes.length === 0) return null
  return (
    <>
      {computes.map((compute) => {
        const Icon = computeIconFor(compute.compute_type)
        return (
          <PlantHref
            key={compute.id}
            id={compute.id}
            label="Compute"
            node={compute}
            className="inline-flex items-center gap-1 rounded-full border border-teal-400/40 bg-teal-500/10 px-2 py-0.5 text-11 font-medium text-teal-700 no-underline hover:bg-teal-500/20 dark:text-teal-300"
            title={`Monitored by ${compute.name}`}
          >
            <Icon size={11} aria-hidden />
            {compute.name}
            <SessionEntityMark entityId={compute.id} />
          </PlantHref>
        )
      })}
    </>
  )
}

const SAFETY_ROLE_LABEL: Record<PlantProtectiveFunctionLinkRole, string> = {
  sensor: 'Sensor',
  final_element: 'Final element',
  protected: 'Protected'
}

function safetyRoleLabel(roles: PlantProtectiveFunctionLinkRole[]): string {
  if (roles.length === 0) return 'Safety'
  return roles.map((role) => SAFETY_ROLE_LABEL[role]).join(' · ')
}

function SafetyFunctionChips({
  links
}: {
  links: PlantEquipmentProtectiveFunctionLink[]
}): React.ReactElement | null {
  if (links.length === 0) return null
  return (
    <>
      {links.map((link) => (
        <Link
          key={link.function.id}
          to={plantProtectiveFunctionHref(link.function.id)}
          className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand-soft px-2 py-0.5 text-11 font-medium text-brand no-underline hover:bg-brand-soft/70"
          title={`${safetyRoleLabel(link.roles)} in ${link.function.name}`}
        >
          <ShieldIcon size={11} aria-hidden />
          {link.function.name}
        </Link>
      ))}
    </>
  )
}

function SafetyPointChips({
  links
}: {
  links: PlantEquipmentSafetyPointLink[]
}): React.ReactElement | null {
  if (links.length === 0) return null
  return (
    <>
      {links.map((link) => (
        <PlantHref
          key={link.point.id}
          id={link.point.id}
          label="SafetyPoint"
          node={link.point}
          className="inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand-soft px-2 py-0.5 text-11 font-medium text-brand no-underline hover:bg-brand-soft/70"
          title={`Safety Manager point ${link.point.name}`}
        >
          <RadioIcon size={11} aria-hidden />
          {link.point.name}
        </PlantHref>
      ))}
    </>
  )
}

function EquipmentSafetyPointsSection({
  links
}: {
  links: PlantEquipmentSafetyPointLink[]
}): React.ReactElement | null {
  if (links.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Safety Manager points"
        subtitle={`${links.length} ${links.length === 1 ? 'point' : 'points'}`}
      />
      <div className="flex flex-col gap-2">
        {links.map((link) => (
          <PlantHref
            key={link.point.id}
            id={link.point.id}
            label="SafetyPoint"
            node={link.point}
            className="flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
              <RadioIcon size={16} aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-ink">
                {link.point.name}
              </span>
              <span className="block truncate text-11 text-ink-3">
                {link.point.point_type ?? 'Safety point'}
                {link.diagram ? ` · ${link.diagram.name}` : ''}
                {link.solver ? ` · ${link.solver.name}` : ''}
              </span>
            </span>
          </PlantHref>
        ))}
      </div>
    </section>
  )
}

function EquipmentSafetySection({
  links
}: {
  links: PlantEquipmentProtectiveFunctionLink[]
}): React.ReactElement {
  return (
    <section>
      <SectionHeader
        label="Safety"
        subtitle={
          links.length > 0
            ? `${links.length} ${links.length === 1 ? 'function' : 'functions'}`
            : undefined
        }
      />
      {links.length === 0 ? (
        <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
          <p className="m-0 text-sm text-ink-3">
            No protective functions are linked to this equipment yet.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-3">
            <Link to={plantSafetyIndexHref()}>
              <ShieldIcon />
              Open Safety Requirements
            </Link>
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {links.map((link) => {
            const typeLabel = protectiveFunctionTypeLabel(link.function.function_type)
            return (
              <Link
                key={link.function.id}
                to={plantProtectiveFunctionHref(link.function.id)}
                className="flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
                  <ShieldIcon size={16} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">
                    {link.function.name}
                  </span>
                  <span className="block truncate text-11 text-ink-3">
                    {safetyRoleLabel(link.roles)}
                    {typeLabel ? ` · ${typeLabel}` : ''}
                    {link.function.sil ? ` · SIL ${link.function.sil}` : ''}
                  </span>
                </span>
              </Link>
            )
          })}
        </div>
      )}
    </section>
  )
}

function RobotControllerChips({
  controllers
}: {
  controllers: PlantRobotControllerRef[]
}): React.ReactElement | null {
  if (!controllers || controllers.length === 0) return null
  return (
    <>
      {controllers.map((controller) => (
        <PlantHref
          key={controller.id}
          id={controller.id}
          label="RobotController"
          node={controller}
          className="inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-11 font-medium text-amber-700 no-underline hover:bg-amber-500/20 dark:text-amber-300"
          title={`Driven by robot controller ${controller.name}`}
        >
          <RobotChipIcon size={11} aria-hidden />
          {controller.name}
          <SessionEntityMark entityId={controller.id} />
        </PlantHref>
      ))}
    </>
  )
}

function PropertiesSection({
  observables,
  setpoints,
  unclassified
}: {
  observables: PlantEquipmentProperty[]
  setpoints: PlantEquipmentProperty[]
  unclassified: PlantEquipmentProperty[]
}): React.ReactElement | null {
  const total = observables.length + setpoints.length + unclassified.length
  if (total === 0) return null
  return (
    <section>
      <SectionHeader
        label="Properties"
        subtitle={
          unclassified.length > 0 ? `${total} · ${unclassified.length} unclassified` : `${total}`
        }
      />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        {observables.length > 0 && <PropertyGroup label="Observables" properties={observables} />}
        {setpoints.length > 0 && <PropertyGroup label="Setpoints" properties={setpoints} />}
        {unclassified.length > 0 && (
          <PropertyGroup label="Unclassified" properties={unclassified} />
        )}
      </div>
    </section>
  )
}

function PropertyGroup({
  label,
  properties
}: {
  label: string
  properties: PlantEquipmentProperty[]
}): React.ReactElement {
  return (
    <div className="mt-3 first:mt-0">
      <div className="mb-1.5 text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
        {label}
      </div>
      <table className="w-full text-left text-13">
        <thead>
          <tr className="border-b text-10 uppercase tracking-[0.04em] text-ink-3">
            <th className="px-2 py-1.5 font-semibold">Property</th>
            <th className="px-2 py-1.5 font-semibold">Type</th>
            <th className="px-2 py-1.5 font-semibold">Tag</th>
            <th className="px-2 py-1.5 font-semibold">Range</th>
          </tr>
        </thead>
        <tbody>
          {properties.map((property) => (
            <PropertyRow key={property.id} property={property} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function PropertyRow({ property }: { property: PlantEquipmentProperty }): React.ReactElement {
  const variant: 'default' | 'outline' | 'secondary' =
    property.kind === 'setpoint'
      ? 'default'
      : property.kind === 'observable'
        ? 'outline'
        : 'secondary'
  // When the tag-linker matched a whole UDT instance instead of a flat
  // Tag, the property's `tag` carries `label === 'UDTInstance'`. We badge
  // the row so the user can tell this is a template-typed signal (and
  // can drill into its members via the "UDT instances" section below).
  const isUdtTarget = property.tag?.label === 'UDTInstance'
  return (
    <tr className="border-b last:border-b-0">
      <td className="px-2 py-1.5 align-top">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-ink">{property.name}</div>
            {property.description && (
              <div className="text-11 text-ink-3">{property.description}</div>
            )}
            <SessionInlinePropertyChanges entityId={property.id} />
          </div>
          <SessionEntityMark entityId={property.id} />
        </div>
      </td>
      <td className="px-2 py-1.5 align-top">
        <Badge variant={variant}>
          {property.kind === 'observable'
            ? 'Observable'
            : property.kind === 'setpoint'
              ? 'Setpoint'
              : 'EquipmentProperty'}
        </Badge>
      </td>
      <td className="px-2 py-1.5 align-top font-mono text-12 text-ink-3">
        {property.tag ? (
          <span className="inline-flex items-center gap-1.5">
            <span>{leafTagName(property.tag.tag_name ?? property.tag.name)}</span>
            {isUdtTarget && (
              <Badge variant="secondary" className="text-10">
                UDT
              </Badge>
            )}
          </span>
        ) : (
          '—'
        )}
        {property.unit && <span className="ml-1 text-ink-4">· {property.unit}</span>}
      </td>
      <td className="px-2 py-1.5 align-top font-mono text-12 text-ink-3">
        {property.range_low ?? '—'} – {property.range_high ?? '—'}
      </td>
    </tr>
  )
}

/**
 * Renders every `UDTInstance` directly materialised by this equipment
 * (`Equipment -[:MATERIALIZES]-> UDTInstance`). Each instance is a
 * collapsible row showing the template type plus its `HAS_MEMBER ->
 * UDTMember` leaf readings (snapshot values captured at structural
 * ingest time). Polling live values for individual members happens
 * separately via the `LiveTagPanel` once we wire each member through
 * the live-data hook.
 */
function UdtInstancesSection({
  instances
}: {
  instances: PlantUdtInstance[]
}): React.ReactElement | null {
  if (instances.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="UDT Instances"
        subtitle={`${instances.length} ${instances.length === 1 ? 'instance' : 'instances'} materialised`}
      />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="flex flex-col gap-2.5">
          {instances.map((instance) => (
            <UdtInstanceRow key={instance.id} instance={instance} />
          ))}
        </div>
      </div>
    </section>
  )
}

function UdtInstanceRow({ instance }: { instance: PlantUdtInstance }): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false)
  const members = instance.members
  const memberCount = members.length
  const Caret = expanded ? CaretDownIcon : CaretRightIcon
  return (
    <div className="rounded-lg border bg-bg-elev">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-accent"
        aria-expanded={expanded}
      >
        <Caret size={12} className="text-ink-3" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-mono text-13 font-semibold text-ink">
            {instance.name}
          </span>
          {instance.type_id && (
            <span className="block truncate font-mono text-11 text-ink-3">{instance.type_id}</span>
          )}
        </span>
        <Badge variant="outline" className="text-10">
          {memberCount} {memberCount === 1 ? 'member' : 'members'}
        </Badge>
        {instance.provider_name && (
          <Badge variant="secondary" className="text-10">
            {instance.provider_name}
          </Badge>
        )}
        <SessionEntityMark entityId={instance.id} />
      </button>
      {expanded && (
        <div className="border-t px-3 py-2">
          {memberCount === 0 ? (
            <div className="px-1 py-1.5 text-12 text-ink-3">
              No leaf members are wired to this UDT instance.
            </div>
          ) : (
            <table className="w-full text-left text-12">
              <thead>
                <tr className="border-b text-10 uppercase tracking-[0.04em] text-ink-3">
                  <th className="px-2 py-1 font-semibold">Member</th>
                  <th className="px-2 py-1 font-semibold">Type</th>
                  <th className="px-2 py-1 font-semibold">Snapshot</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <UdtMemberRow key={member.id} member={member} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}

function UdtMemberRow({ member }: { member: PlantUdtMember }): React.ReactElement {
  const value = member.value ?? '—'
  return (
    <tr className="border-b last:border-b-0">
      <td className="px-2 py-1 align-top font-mono text-12 text-ink">
        <span className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate">{member.name}</span>
          <SessionEntityMark entityId={member.id} />
        </span>
      </td>
      <td className="px-2 py-1 align-top font-mono text-11 text-ink-3">
        {member.member_type ?? '—'}
        {member.data_type && <span className="ml-1 text-ink-4">· {member.data_type}</span>}
      </td>
      <td className="px-2 py-1 align-top font-mono text-11 text-ink-2">
        {/* Snapshot at ingest time. Live polling for member readings is a future enhancement
            wired through `member.pollPath` once `LiveTagPanel` accepts non-Tag entries. */}
        <span className="line-clamp-1 max-w-65" title={value}>
          {value}
        </span>
        {member.value != null && member.eng_unit && (
          <span className="ml-1 text-ink-4">{member.eng_unit}</span>
        )}
      </td>
    </tr>
  )
}

function SpecsSection({ equipment }: { equipment: PlantEquipment }): React.ReactElement | null {
  const specs = collectEquipmentSpecs(equipment)
  if (specs.length === 0) return null
  return (
    <section>
      <SectionHeader label="Specifications" />
      <div className="rounded-xl border bg-card p-5 text-card-foreground shadow-sm">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-[repeat(auto-fill,minmax(11.25rem,1fr))]">
          {specs.map(([k, v]) => (
            <div key={k} className="min-w-0">
              <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-3">
                {k}
              </div>
              <div className="mt-0.5 [overflow-wrap:anywhere] text-13 font-medium text-ink">
                {v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

function leafTagName(name: string): string {
  const idx = name.lastIndexOf('/')
  return idx >= 0 ? name.slice(idx + 1) : name
}

export default EquipmentDetailPage
